import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

/**
 * Route guard that only lets a request through when the user has an
 * active magic-link handover session (__Host-jcc_handover_session cookie).
 *
 * Members no longer use passwords, so an unauthenticated visit must NOT
 * be sent to /login. It is sent to the existing magic-link handover
 * request flow, with the original destination preserved as ?next=<url>.
 * MagicLinkChecking.jsx picks that value out of localStorage and returns
 * the user to it after they redeem the one-time email link.
 *
 * Room-level access checks (who is a participant of which room) remain
 * enforced server-side by /api/care-rooms/* — this guard only blocks
 * anonymous visits.
 */
export default function HandoverProtectedRoute({ children }) {
  const location = useLocation();
  const [state, setState] = useState("checking"); // checking | ok | anon

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/handover/me`,
          { credentials: "include" }
        );
        if (cancelled) return;
        if (!r.ok) { setState("anon"); return; }
        const body = await r.json().catch(() => ({}));
        setState(body && body.active ? "ok" : "anon");
      } catch {
        if (!cancelled) setState("anon");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state === "checking") {
    return (
      <div
        className="container-jcc py-24 text-center text-ink-muted"
        data-testid="handover-protected-checking"
      >
        Loading…
      </div>
    );
  }

  if (state === "anon") {
    const next = `${location.pathname}${location.search || ""}`;
    return (
      <Navigate
        to={`/auth/handover/request?next=${encodeURIComponent(next)}`}
        replace
      />
    );
  }

  return children;
}
